import { Events } from './events.js';
import { Rng } from './rng.js';
import { Time } from './time.js';

/**
 * The context object — a service locator passed to every subsystem.
 *
 * Modules resolve their collaborators at runtime through `ctx.get(id)` rather
 * than importing each other. This exists to keep the dependency graph flat: any
 * subsystem can reach any other without creating an import cycle, and each
 * directory can be developed and reasoned about in isolation.
 *
 * The cost is that a typo in an id becomes a runtime error instead of a build
 * error, so `get` throws loudly with the list of what *is* registered rather
 * than returning undefined and failing three frames later somewhere unrelated.
 */
export class Ctx {
  constructor({ scene, camera, viewScene, viewCamera, canvas, config }) {
    this.scene = scene;             // world scene
    this.camera = camera;           // world camera
    this.viewScene = viewScene;     // viewmodel scene, rendered after, never occluded
    this.viewCamera = viewCamera;   // viewmodel camera, separate FOV from the world
    this.canvas = canvas;
    this.config = config;

    this.events = new Events();
    this.time = new Time();
    this.rng = new Rng(config.seed);

    /** @type {Map<string, any>} */
    this._modules = new Map();
    /** @type {Array<{id:string, mod:any}>} */
    this._ordered = [];

    // Shared mutable game state that genuinely is cross-cutting. Kept small on
    // purpose — anything that belongs to one subsystem stays in that subsystem.
    this.state = {
      paused: false,
      playerDead: false,
      missionComplete: false,
      inCombat: false,
      encounter: null,
    };
  }

  register(id, mod) {
    if (this._modules.has(id)) throw new Error(`[ctx] duplicate module id "${id}"`);
    this._modules.set(id, mod);
    this._ordered.push({ id, mod });
    return mod;
  }

  get(id) {
    const m = this._modules.get(id);
    if (!m) {
      throw new Error(
        `[ctx] no module "${id}". Registered: ${[...this._modules.keys()].join(', ')}`
      );
    }
    return m;
  }

  /** Non-throwing lookup, for genuinely optional collaborators. */
  peek(id) {
    return this._modules.get(id) ?? null;
  }

  has(id) {
    return this._modules.has(id);
  }

  get modules() {
    return this._ordered;
  }
}
